import { BrokerInactiveNotice } from '@/features/brokers/components/broker-inactive-notice.component';
import { useSelectedBrokerWriteGuard } from '@/features/brokers/hooks';
import { Page, PageHeader } from '@/shared/ui/page';
import { Spinner, Text } from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import { Home } from 'lucide-react';
import { useEffect } from 'react';
import { useProposal } from '../hooks/use-proposal.hook';
import {
  getProposalCategory,
  getProposalCategoryLabel,
} from '../utils/proposal-category';
import { isCapitalGlobalProposalProduct } from './capital-global/capital-global-proposal-product.util';
import { NewProposalForm } from './new-proposal-form.component';

type NewProposalPageProps = {
  policyId?: string;
  mode?: 'edit' | 'view';
};

export function NewProposalPage({
  policyId,
  mode = 'edit',
}: NewProposalPageProps) {
  const navigate = useNavigate();
  const { data: proposal, isLoading: isLoadingProposal } =
    useProposal(policyId);
  // Edição fica atrás do BrokerWriteGate; aqui o aviso é da visualização
  // (e-mail, PDF e transmitir travados).
  const proposalActionGuard = useSelectedBrokerWriteGuard('proposal', 'other');

  const shouldRedirectToCapitalGlobal =
    proposal != null && isCapitalGlobalProposalProduct(proposal.productCode);

  useEffect(() => {
    if (!shouldRedirectToCapitalGlobal || !policyId) return;
    navigate({
      to:
        mode === 'edit'
          ? '/proposals/$id/capital-global/edit'
          : '/proposals/$id/capital-global',
      params: { id: policyId },
      replace: true,
    });
  }, [shouldRedirectToCapitalGlobal, policyId, mode, navigate]);

  if (policyId && isLoadingProposal && !proposal) {
    return (
      <Page.Root>
        <Page.Breadcrumb
          items={[
            {
              label: 'Página Inicial',
              href: '/dashboard',
              iconLeft: <Home className='h-4 w-4' />,
            },
            { label: 'Comercial' },
            { label: 'Propostas', href: '/proposals' },
            { label: 'Proposta', active: true },
          ]}
        />
        <PageHeader.Root>
          <PageHeader.Main>
            <PageHeader.TitleGroup>
              <PageHeader.Title>Proposta</PageHeader.Title>
              <PageHeader.Subtitle>
                Preencha as informações para efetuar a Proposta
              </PageHeader.Subtitle>
            </PageHeader.TitleGroup>
          </PageHeader.Main>
        </PageHeader.Root>
        <Page.Content className='py-4'>
          <div className='flex items-center justify-center py-16'>
            <div className='flex items-center gap-3'>
              <Spinner size='sm' colorScheme='gray' />
              <Text className='text-sm text-alba-gray-600'>
                Carregando proposta...
              </Text>
            </div>
          </div>
        </Page.Content>
      </Page.Root>
    );
  }

  if (shouldRedirectToCapitalGlobal) {
    return (
      <Page.Root>
        <Page.Content className='py-4'>
          <div className='flex items-center justify-center py-16'>
            <div className='flex items-center gap-3'>
              <Spinner size='sm' colorScheme='gray' />
              <Text className='text-sm text-alba-gray-600'>
                Redirecionando...
              </Text>
            </div>
          </div>
        </Page.Content>
      </Page.Root>
    );
  }

  const category = getProposalCategory(proposal?.productCode);
  const categoryLabel = getProposalCategoryLabel(category);

  return (
    <Page.Root>
      <Page.Breadcrumb
        items={[
          {
            label: 'Página Inicial',
            href: '/dashboard',
            iconLeft: <Home className='h-4 w-4' />,
          },
          { label: 'Comercial' },
          { label: 'Propostas', href: '/proposals' },
          { label: 'Proposta', active: true },
        ]}
      />
      <PageHeader.Root>
        <PageHeader.Main>
          <PageHeader.TitleGroup>
            <PageHeader.Title>Proposta - {categoryLabel}</PageHeader.Title>
            <PageHeader.Subtitle>
              Preencha as informações para efetuar a Proposta
            </PageHeader.Subtitle>
          </PageHeader.TitleGroup>
        </PageHeader.Main>
      </PageHeader.Root>
      <BrokerInactiveNotice guards={[proposalActionGuard]} />
      <Page.Content className='py-4'>
        <NewProposalForm mode={mode} policyId={policyId} />
      </Page.Content>
    </Page.Root>
  );
}
